import React, { useState, useRef, useEffect, useCallback } from 'react';
import ImageWithFallback from '../ImageWithFallback';
import { API_BASE_URL } from '../../config/api';
import './ImageCarousel.css';

interface ImageCarouselProps {
    images: string[];
    alt: string;
    className?: string;
    onImageClick?: () => void;
    maxInitialImages?: number; // New prop to limit initial image loading
    onIndexChange?: (index: number) => void; // Callback when index changes
    onPrimaryImageLoad?: () => void; // Callback when primary (first) image loads
    onImageLoad?: () => void; // Callback when any image loads (for tracking when post can be shown)
    onFirstTwoImagesLoad?: () => void; // Callback when first 2 images are loaded
    loadOnlyPrimary?: boolean; // If true, only load the first image initially
    loadFirstTwo?: boolean; // If true, only load the first 2 images initially
    fallbackImages?: string[]; // Fallback image URLs if primary images fail to load
    hdImages?: string[]; // HD versions of images to load after compressed images are loaded
    fetchPriority?: 'high' | 'low' | 'auto'; // Priority for the primary image
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ 
    images, 
    alt, 
    className = '', 
    onImageClick,
    maxInitialImages = undefined,
    onIndexChange,
    onPrimaryImageLoad,
    onImageLoad,
    onFirstTwoImagesLoad,
    loadOnlyPrimary = false,
    loadFirstTwo = false,
    fallbackImages = [],
    hdImages = undefined,
    fetchPriority = 'auto'
}) => {
    const [firstTwoImagesLoadedCount, setFirstTwoImagesLoadedCount] = useState(0);
    const firstTwoLoadedRef = useRef(false);
    const primaryLoadedRef = useRef(false);
    const primaryImageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    // Reset count when images change or loadFirstTwo changes
    useEffect(() => {
        if (loadFirstTwo) {
            setFirstTwoImagesLoadedCount(0);
            firstTwoLoadedRef.current = false;
        }
    }, [images, loadFirstTwo]);
    
    // Reset primary loaded ref when loadOnlyPrimary changes
    useEffect(() => {
        if (loadOnlyPrimary) {
            primaryLoadedRef.current = false;
            // Clear any existing timeout
            if (primaryImageTimeoutRef.current) {
                clearTimeout(primaryImageTimeoutRef.current);
                primaryImageTimeoutRef.current = null;
            }
        }
    }, [images, loadOnlyPrimary]);
    
    // Check for cached primary image when loadOnlyPrimary is true
    useEffect(() => {
        if (loadOnlyPrimary && images.length > 0 && onPrimaryImageLoad && !primaryLoadedRef.current) {
            // Single check for cached image after a short delay
            const checkTimer = setTimeout(() => {
                if (primaryLoadedRef.current) return;
                
                const imageElements = carouselRef.current?.querySelectorAll('img');
                if (imageElements && imageElements.length > 0) {
                    const firstImg = imageElements[0] as HTMLImageElement;
                    if (firstImg && firstImg.complete && firstImg.naturalHeight !== 0) {
                        primaryLoadedRef.current = true;
                        onPrimaryImageLoad();
                    }
                }
            }, 100);
            
            return () => clearTimeout(checkTimer);
        }
    }, [loadOnlyPrimary, images, onPrimaryImageLoad]);
    
    // Set timeout for primary image loading - CRITICAL: ensures callback always fires
    useEffect(() => {
        if (loadOnlyPrimary && images.length > 0 && onPrimaryImageLoad && !primaryLoadedRef.current) {
            // Set a timeout to force the callback after 8 seconds if image hasn't loaded
            // This prevents posts from being stuck in loading state forever
            primaryImageTimeoutRef.current = setTimeout(() => {
                if (!primaryLoadedRef.current && onPrimaryImageLoad) {
                    console.warn('Primary image load timeout - forcing callback to prevent stuck loading state');
                    primaryLoadedRef.current = true;
                    onPrimaryImageLoad();
                }
            }, 8000); // 8 second timeout
            
            return () => {
                if (primaryImageTimeoutRef.current) {
                    clearTimeout(primaryImageTimeoutRef.current);
                    primaryImageTimeoutRef.current = null;
                }
            };
        }
    }, [loadOnlyPrimary, images, onPrimaryImageLoad]);
    
    // Check for cached images once after component mounts
    useEffect(() => {
        if (loadFirstTwo && images.length > 0 && onFirstTwoImagesLoad && !firstTwoLoadedRef.current) {
            // Single check for cached images after a short delay
            const checkTimer = setTimeout(() => {
                if (firstTwoLoadedRef.current) return;
                
                const imageElements = carouselRef.current?.querySelectorAll('img');
                if (imageElements && imageElements.length > 0) {
                    let loadedCount = 0;
                    const imagesToCheck = Math.min(2, images.length);
                    
                    imageElements.forEach((img, idx) => {
                        if (idx < imagesToCheck) {
                            const imgEl = img as HTMLImageElement;
                            if (imgEl.complete && imgEl.naturalHeight !== 0) {
                                loadedCount++;
                            }
                        }
                    });
                    
                    if (loadedCount >= imagesToCheck && !firstTwoLoadedRef.current) {
                        firstTwoLoadedRef.current = true;
                        setFirstTwoImagesLoadedCount(imagesToCheck);
                        onFirstTwoImagesLoad();
                    }
                }
            }, 100);
            
            return () => clearTimeout(checkTimer);
        }
    }, [loadFirstTwo, images, onFirstTwoImagesLoad]);
    const [currentIndex, setCurrentIndex] = useState(0); // Start at index 0 (first image)
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
    const [hdLoadedImages, setHdLoadedImages] = useState<Set<number>>(new Set());
    
    const totalImages = images.length; // Keep track of total for counter display
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isSeamlessTransition, setIsSeamlessTransition] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const carouselRef = useRef<HTMLDivElement>(null);

    // Reset HD loaded state when hdImages changes
    useEffect(() => {
        if (!hdImages || hdImages.length === 0) {
            setHdLoadedImages(new Set());
        }
    }, [hdImages]);

    // Initialize onIndexChange callback
    useEffect(() => {
        if (images.length > 0 && onIndexChange) {
            onIndexChange(0);
        }
    }, [images, onIndexChange]);

    // Load images as user navigates
    useEffect(() => {
        const loadImage = (index: number) => {
            if (index >= 0 && index < images.length && !loadedImages.has(index)) {
                const img = new Image();
                img.src = images[index].startsWith('http') ? 
                    images[index] : 
                    `${API_BASE_URL}${images[index]}`;
                img.onload = () => {
                    setLoadedImages(prev => {
                        const newSet = new Set(prev);
                        newSet.add(index);
                        return newSet;
                    });
                };
            }
        };

        // Load current image and adjacent images
        const prevIndex = currentIndex - 1;
        const nextIndex = currentIndex + 1;
        
        [prevIndex, currentIndex, nextIndex].forEach(index => {
            if (index >= 0 && index < images.length) {
                loadImage(index);
            }
        });
    }, [currentIndex, images, loadedImages]);

    const goToNext = useCallback(() => {
        if (isTransitioning || currentIndex >= images.length - 1) return;
        setIsTransitioning(true);
        setCurrentIndex((prev) => {
            const nextIndex = prev + 1;
            // Stop at the last image - no looping
            if (nextIndex >= images.length) {
                setIsTransitioning(false);
                return prev; // Stay at current position
            }
            if (onIndexChange) onIndexChange(nextIndex);
            return nextIndex;
        });
        setTimeout(() => setIsTransitioning(false), 300);
    }, [images.length, isTransitioning, currentIndex, onIndexChange]);

    const goToPrevious = useCallback(() => {
        if (isTransitioning || currentIndex <= 0) return;
        setIsTransitioning(true);
        setCurrentIndex((prev) => {
            const prevIndex = prev - 1;
            // Stop at the first image - no looping
            if (prevIndex < 0) {
                setIsTransitioning(false);
                return prev; // Stay at current position
            }
            if (onIndexChange) onIndexChange(prevIndex);
            return prevIndex;
        });
        setTimeout(() => setIsTransitioning(false), 300);
    }, [isTransitioning, currentIndex, onIndexChange]);

    // Helper function to get the real image index for display purposes
    const getRealImageIndex = () => {
        return currentIndex;
    };

    // Touch handlers for mobile swipe with drag animation
    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsDragging(true);
        setDragOffset(0);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchStart || !isDragging) return;
        
        const currentTouch = e.targetTouches[0].clientX;
        const diff = currentTouch - touchStart; // Reversed: positive when swiping right
        
        // Allow both forward and backwards drag movement
        const maxOffset = carouselRef.current?.offsetWidth || 300;
        const limitedOffset = Math.max(-maxOffset * 0.3, Math.min(maxOffset * 0.3, diff));
        
        setDragOffset(limitedOffset);
        setTouchEnd(currentTouch);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd || !isDragging) {
            setIsDragging(false);
            setDragOffset(0);
            return;
        }
        
        const distance = touchEnd - touchStart; // Reversed: positive when swiping right
        const isLeftSwipe = distance < -50;
        const isRightSwipe = distance > 50;

        // Reset drag state
        setIsDragging(false);
        setDragOffset(0);

        if (isLeftSwipe) {
            goToNext(); // Left swipe goes to next
        } else if (isRightSwipe) {
            goToPrevious(); // Right swipe goes to previous
        }
        
        // Reset touch state
        setTouchStart(null);
        setTouchEnd(null);
    };

    // Keyboard navigation - both directions
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (carouselRef.current?.contains(document.activeElement)) {
                if (e.key === 'ArrowLeft') {
                    goToPrevious();
                } else if (e.key === 'ArrowRight') {
                    goToNext();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [goToNext, goToPrevious]);

    if (!images || images.length === 0) {
        return (
            <div className={`image-carousel ${className}`}>
                <ImageWithFallback src={undefined} alt={alt} />
            </div>
        );
    }

    return (
        <div 
            ref={carouselRef}
            className={`image-carousel ${className}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={onImageClick}
            tabIndex={0}
            role="img"
            aria-label={`${alt} - Image ${getRealImageIndex() + 1} of ${totalImages}`}
        >
            <div className="carousel-container">
                <div 
                    className="carousel-images-track"
                    style={{
                        transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`,
                        transition: isDragging ? 'none' : 'transform 0.3s ease'
                    }}
                >
                    {images.map((image, imageIndex) => {
                        // If loadOnlyPrimary is true, only render the first image
                        if (loadOnlyPrimary && imageIndex > 0) {
                            return null;
                        }
                        
                        // If loadFirstTwo is true, only render the first 2 images
                        if (loadFirstTwo && imageIndex > 1) {
                            return null;
                        }
                        
                        const isFirst = imageIndex === 0;
                        const isSecond = imageIndex === 1;
                        const isLast = imageIndex === images.length - 1;
                        const wrapperClass = `carousel-image-wrapper ${isFirst ? 'carousel-image-first' : ''} ${isLast ? 'carousel-image-last' : ''}`.trim();
                        
                        // Get fallback image if available
                        const fallbackImage = fallbackImages && fallbackImages.length > imageIndex ? fallbackImages[imageIndex] : null;
                        const imageUrl = image.startsWith('http') ? image : `${API_BASE_URL}${image}`;
                        const fallbackImageUrl = fallbackImage ? (fallbackImage.startsWith('http') ? fallbackImage : `${API_BASE_URL}${fallbackImage}`) : undefined;
                        
                        // Check if HD version is available and loaded
                        const hasHdVersion = hdImages && hdImages.length > imageIndex && hdImages[imageIndex];
                        const hdIsLoaded = hasHdVersion && hdLoadedImages.has(imageIndex);
                        const hdImageUrl = hasHdVersion 
                            ? (hdImages[imageIndex].startsWith('http') ? hdImages[imageIndex] : `${API_BASE_URL}${hdImages[imageIndex]}`)
                            : null;

                        return (
                            <div key={imageIndex} className={wrapperClass}>
                                {/* Compressed image - always shown */}
                                <ImageWithFallback
                                    src={imageUrl}
                                    fallbackSrc={fallbackImageUrl}
                                    alt={`${alt} - Image ${imageIndex + 1}`}
                                    className={`carousel-image carousel-image-compressed ${hdIsLoaded ? 'hd-ready' : ''}`}
                                    loading={isFirst ? "eager" : "lazy"}
                                    fetchPriority={isFirst ? fetchPriority : 'auto'}
                                    onError={() => {
                                        // If compressed image fails and we have a fallback, try it
                                        if (fallbackImageUrl) {
                                            // The ImageWithFallback component will handle the fallback automatically
                                            console.log(`Image ${imageIndex + 1} failed, trying fallback`);
                                        }
                                        // CRITICAL: If first image fails, still call onPrimaryImageLoad to prevent stuck loading
                                        if (isFirst && onPrimaryImageLoad && !primaryLoadedRef.current) {
                                            console.warn('First image failed to load - forcing callback to prevent stuck loading state');
                                            primaryLoadedRef.current = true;
                                            // Clear timeout since we're handling it now
                                            if (primaryImageTimeoutRef.current) {
                                                clearTimeout(primaryImageTimeoutRef.current);
                                                primaryImageTimeoutRef.current = null;
                                            }
                                            onPrimaryImageLoad();
                                        }
                                    }}
                                    onLoad={() => {
                                        // Call primary image load callback if this is the first image
                                        if (isFirst && onPrimaryImageLoad && !primaryLoadedRef.current) {
                                            primaryLoadedRef.current = true;
                                            // Clear timeout since image loaded successfully
                                            if (primaryImageTimeoutRef.current) {
                                                clearTimeout(primaryImageTimeoutRef.current);
                                                primaryImageTimeoutRef.current = null;
                                            }
                                            onPrimaryImageLoad();
                                        }
                                        // Call general image load callback for tracking when post can be shown
                                        if (isFirst && onImageLoad) {
                                            onImageLoad();
                                        }
                                        // Track first 2 images loading
                                        if (loadFirstTwo && (isFirst || isSecond) && !firstTwoLoadedRef.current) {
                                            setFirstTwoImagesLoadedCount(prev => {
                                                const newCount = prev + 1;
                                                // If both first 2 images are loaded, or if only 1 image exists and it's loaded, call callback
                                                const requiredCount = images.length >= 2 ? 2 : 1;
                                                if (newCount >= requiredCount && onFirstTwoImagesLoad && !firstTwoLoadedRef.current) {
                                                    firstTwoLoadedRef.current = true;
                                                    // Call callback immediately
                                                    onFirstTwoImagesLoad();
                                                }
                                                return newCount;
                                            });
                                        }
                                    }}
                                />
                                {/* Chrome scroll fix: HD image disabled to prevent re-renders during scroll */}
                            </div>
                        );
                    })}
                </div>
                
                {/* Navigation buttons - both directions */}
                {totalImages > 1 && (
                    <>
                        <button
                            className="carousel-nav-btn carousel-nav-left"
                            onClick={(e) => {
                                e.stopPropagation();
                                goToPrevious();
                            }}
                            aria-label="Previous image"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        
                        <button
                            className="carousel-nav-btn carousel-nav-right"
                            onClick={(e) => {
                                e.stopPropagation();
                                goToNext();
                            }}
                            aria-label="Next image"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </>
                )}
                
                {/* Image counter (always show, even with one image) */}
                {totalImages > 0 && (
                    <div className="image-counter">
                        {getRealImageIndex() + 1} / {totalImages}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageCarousel;
